import api from '../../../utils/api';
import {
    createSingleFileFormData,
    mergeUploadResults,
    toFileArray
} from '@/utils/fileUploadQueue';

function godModeParams(godMode) {
    return godMode === true ? { god_mode: true } : {};
}

function withGodMode(payload, godMode) {
    return {
        ...payload,
        god_mode: godMode === true
    };
}

/**
 * API для работы с карточкой заявки
 */
export default {
    /**
     * Получить заявку по ID
     */
    getRequest(id) {
        return api.get(`/api/requests/items/${id}`).then(({ data }) => data);
    },

    /**
     * Получить активность заявки (комментарии и история)
     */
    getRequestActivity(id) {
        return api.get(`/api/requests/items/${id}/activity`).then(({ data }) => data);
    },

    /**
     * Получить доступные действия для заявки
     */
    getAvailableActions(id) {
        return api.get(`/api/requests/items/${id}/available-actions`).then(({ data }) => data);
    },

    /**
     * Получить список доступных исполнителей для ручного назначения
     */
    getAvailableExecutors(requestId) {
        return api.get(`/api/requests/items/${requestId}/available-executors`).then(({ data }) => data);
    },

    /**
     * Получить объекты, доступные для смены в карточке заявки
     */
    getBuildingProcessOptions(requestId) {
        return api.get(`/api/requests/items/${requestId}/building-process-options`).then(({ data }) => data);
    },

    /**
     * Получить сотрудников для замены согласующего
     */
    getApproverReplacementOptions(requestId, participantId, godMode = false) {
        return api
            .get(`/api/requests/items/${requestId}/approvers/${participantId}/replacement-options`, {
                params: godModeParams(godMode)
            })
            .then(({ data }) => data);
    },

    /**
     * Получить сотрудников для добавления согласующего
     */
    getApproverAddOptions(requestId, godMode = false) {
        return api
            .get(`/api/requests/items/${requestId}/approvers/add-options`, {
                params: godModeParams(godMode)
            })
            .then(({ data }) => data);
    },

    /**
     * Получить сотрудников для замены участника процесса
     */
    getParticipantReplacementOptions(requestId, participantId) {
        return api
            .get(`/api/requests/items/${requestId}/participants/${participantId}/replacement-options`)
            .then(({ data }) => data);
    },

    /**
     * Получить сотрудников для добавления участника процесса
     */
    getParticipantAddOptions(requestId, role) {
        return api
            .get(`/api/requests/items/${requestId}/participants/${role}/add-options`)
            .then(({ data }) => data);
    },

    /**
     * Обновить заявку
     */
    updateRequest(id, payload, options = {}) {
        return api.put(`/api/requests/items/${id}`, payload, options).then(({ data }) => data);
    },

    /**
     * Загрузить вложения к существующей заявке
     */
    async uploadRequestFiles(id, files = []) {
        const results = [];
        for (const file of toFileArray(files)) {
            const formData = createSingleFileFormData('attachments', file);
            const { data } = await api.post(`/api/requests/items/${id}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            results.push(data);
        }
        return mergeUploadResults(results);
    },

    /**
     * Обновить позицию заявки
     */
    updateRequestItem(requestId, itemId, payload) {
        return api.put(`/api/requests/items/${requestId}/items/${itemId}`, payload).then(({ data }) => data);
    },

    /**
     * Отправить заявку на согласование
     */
    submitRequest(id, comment = null, expectedUpdatedAt = null) {
        const payload = {...(comment ? { comment } : {}), ...(expectedUpdatedAt ? {expected_updated_at: expectedUpdatedAt} : {})};
        return api.post(`/api/requests/items/${id}/submit`, payload).then(({ data }) => data);
    },

    /**
     * Согласовать заявку
     */
    approveRequest(id, comment = null) {
        const payload = comment ? { comment } : {};
        return api.post(`/api/requests/items/${id}/approve`, payload).then(({ data }) => data);
    },

    /**
     * Отклонить заявку
     */
    rejectRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/reject`, { comment }).then(({ data }) => data);
    },

    /**
     * Взять заявку в работу
     */
    executeRequest(id, comment = null) {
        const payload = comment ? { comment } : {};
        return api.post(`/api/requests/items/${id}/execute`, payload).then(({ data }) => data);
    },

    /**
     * Вернуть заявку на доработку
     */
    returnRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/return`, { comment }).then(({ data }) => data);
    },

    /**
     * Завершить выполнение заявки (на приемку)
     */
    completeRequest(id, comment = null, fulfillment = null) {
        const payload = { comment, fulfillment };
        return api.post(`/api/requests/items/${id}/complete`, payload).then(({ data }) => data);
    },

    /**
     * Частично выполнить заявку
     */
    partiallyCompleteRequest(id, comment = null, fulfillment = null) {
        const payload = { comment, fulfillment };
        return api.post(`/api/requests/items/${id}/partially_complete`, payload).then(({ data }) => data);
    },

    /**
     * Принять выполненную заявку
     */
    acceptRequest(id, comment = null, fulfillment = null) {
        const payload = { comment, fulfillment };
        return api.post(`/api/requests/items/${id}/accept`, payload).then(({ data }) => data);
    },

    /**
     * Принять частично выполненную заявку
     */
    acceptPartiallyRequest(id, comment = null, fulfillment = null) {
        const payload = { comment, fulfillment };
        return api.post(`/api/requests/items/${id}/accept_partially`, payload).then(({ data }) => data);
    },

    /**
     * Отменить заявку
     */
    cancelRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/cancel`, { comment }).then(({ data }) => data);
    },

    /**
     * Изменить статус заявки напрямую (только для администратора)
     */
    changeRequestStatus(id, targetStatus, comment = null, closeWithoutMechanicActions = false) {
        const payload = { target_status: targetStatus };
        if (closeWithoutMechanicActions === true) {
            payload.close_without_mechanic_actions = true;
            payload.god_mode = true;
        }
        if (comment) {
            payload.comment = comment;
        }
        return api.post(`/api/requests/items/${id}/change-status`, payload).then(({ data }) => data);
    },

    /**
     * Загрузить обязательный документ по правилу типа заявки
     */
    async uploadRequiredAttachment(id, fileKind, files) {
        const results = [];
        for (const file of toFileArray(files)) {
            const formData = createSingleFileFormData('attachments', file, { file_kind: fileKind });
            const { data } = await api.post(`/api/requests/items/${id}/required-attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            results.push(data);
        }
        return mergeUploadResults(results);
    },

    /**
     * Назначить исполнителя на заявку
     */
    assignRequest(id, executorId, comment = null) {
        const payload = { executor_id: executorId };
        if (comment) payload.comment = comment;
        return api.post(`/api/requests/items/${id}/assign`, payload).then(({ data }) => data);
    },

    /**
     * Заменить согласующего в конкретной заявке
     */
    replaceApprover(requestId, participantId, newUserId, reason = null, suppressNotification = false, godMode = false) {
        return api
            .post(`/api/requests/items/${requestId}/approvers/${participantId}/replace`, withGodMode({
                new_user_id: newUserId,
                reason,
                suppress_notification: suppressNotification === true
            }, godMode))
            .then(({ data }) => data);
    },

    /**
     * Удалить согласующего из конкретной заявки
     */
    removeApprover(requestId, participantId, reason = null, suppressNotification = false, godMode = false) {
        return api
            .post(`/api/requests/items/${requestId}/approvers/${participantId}/remove`, withGodMode({
                reason,
                suppress_notification: suppressNotification === true
            }, godMode))
            .then(({ data }) => data);
    },

    /**
     * Добавить согласующего в конкретную заявку
     */
    addApprover(requestId, newUserId, reason = null, suppressNotification = false, godMode = false) {
        return api
            .post(`/api/requests/items/${requestId}/approvers/add`, withGodMode({
                new_user_id: newUserId,
                reason,
                suppress_notification: suppressNotification === true
            }, godMode))
            .then(({ data }) => data);
    },

    /**
     * Заменить участника процесса в конкретной заявке
     */
    replaceParticipant(requestId, participantId, newUserId, reason = null, suppressNotification = false) {
        return api
            .post(`/api/requests/items/${requestId}/participants/${participantId}/replace`, {
                new_user_id: newUserId,
                reason,
                suppress_notification: suppressNotification === true
            })
            .then(({ data }) => data);
    },

    /**
     * Удалить участника процесса из конкретной заявки
     */
    removeParticipant(requestId, participantId, reason = null, suppressNotification = false) {
        return api
            .post(`/api/requests/items/${requestId}/participants/${participantId}/remove`, {
                reason,
                suppress_notification: suppressNotification === true
            })
            .then(({ data }) => data);
    },

    /**
     * Добавить участника процесса в конкретную заявку
     */
    addParticipant(requestId, role, newUserId, reason = null, suppressNotification = false) {
        return api
            .post(`/api/requests/items/${requestId}/participants/add`, {
                role,
                new_user_id: newUserId,
                reason,
                suppress_notification: suppressNotification === true
            })
            .then(({ data }) => data);
    },

    /**
     * Включить режим «несколько исполнителей» и распределить материалы.
     * @param {number} requestId
     * @param {Array<{item_id:number,user_id:number}>} assignments
     */
    assignMultiExecutors(requestId, assignments) {
        return api
            .post(`/api/requests/items/${requestId}/assign-multi`, { assignments })
            .then(({ data }) => data);
    },

    /**
     * Переназначить исполнителя одной строки материала (multi-режим).
     */
    reassignItemExecutor(requestId, itemId, newExecutorId) {
        return api
            .post(`/api/requests/items/${requestId}/items/${itemId}/assign-executor`, {
                executor_id: newExecutorId
            })
            .then(({ data }) => data);
    },

    bulkReassignItemExecutor(requestId, itemIds, newExecutorId) {
        return api
            .post(`/api/requests/items/${requestId}/items/bulk-assign-executor`, {
                item_ids: itemIds,
                executor_id: newExecutorId
            })
            .then(({ data }) => data);
    },

    selfAssignItemExecutor(requestId, itemId) {
        return api
            .post(`/api/requests/items/${requestId}/items/${itemId}/self-assign`)
            .then(({ data }) => data);
    },

    selfReleaseItemExecutor(requestId, itemId) {
        return api
            .post(`/api/requests/items/${requestId}/items/${itemId}/self-release`)
            .then(({ data }) => data);
    },

    selfAssignBulk(requestId) {
        return api
            .post(`/api/requests/items/${requestId}/items/self-assign-bulk`)
            .then(({ data }) => data);
    },

    /**
     * Массовое обновление материалов (добавление/обновление/удаление)
     */
    bulkUpdateMaterials(requestId, items) {
        return api.put(`/api/requests/items/${requestId}/items/bulk-update`, { items }).then(({ data }) => data);
    },

    receiveMechanicAsset(requestId, itemId, payload) {
        return api
            .post(`/api/requests/items/${requestId}/items/${itemId}/mechanic-assets/receipt`, payload)
            .then(({ data }) => data);
    },

    getFuelCardOptions(requestId) {
        return api.get(`/api/requests/items/${requestId}/fuel-card-options`).then(({ data }) => data);
    },

    selectFuelCardForItem(requestId, itemId, fuelCardId) {
        return api
            .put(`/api/requests/items/${requestId}/items/${itemId}/fuel-card`, { fuel_card_id: fuelCardId })
            .then(({ data }) => data);
    },

    getFuelCardTargetObjectOptions(requestId) {
        return api
            .get(`/api/requests/items/${requestId}/fuel-card-target-object-options`)
            .then(({ data }) => data);
    },

    selectFuelCardTargetObjectForItem(requestId, itemId, buildingProcessId) {
        return api
            .put(`/api/requests/items/${requestId}/items/${itemId}/fuel-card-target-object`, {
                building_process_id: buildingProcessId
            })
            .then(({ data }) => data);
    },

    updateFuelCardLimitsForItem(requestId, itemId, fuelLimits) {
        return api
            .put(`/api/requests/items/${requestId}/items/${itemId}/fuel-card-limits`, {
                fuel_limits: fuelLimits
            })
            .then(({ data }) => data);
    },

    /**
     * Скачать список позиций заявки в Excel
     */
    exportMaterials(requestId) {
        return api.get(`/api/requests/items/${requestId}/export-materials`, { responseType: 'blob' });
    },

    /**
     * Добавить комментарий к заявке
     */
    addComment(requestId, text) {
        return api.post(`/api/requests/items/${requestId}/comments`, { text }).then(({ data }) => data);
    },

    /**
     * Обновить комментарий
     */
    updateComment(requestId, commentId, text) {
        return api.put(`/api/requests/items/${requestId}/comments/${commentId}`, { text }).then(({ data }) => data);
    },

    /**
     * Удалить комментарий
     */
    deleteComment(requestId, commentId) {
        return api.delete(`/api/requests/items/${requestId}/comments/${commentId}`).then(({ data }) => data);
    },

    /**
     * Прикрепить файл к комментарию
     */
    addCommentFile(requestId, commentId, file, options = {}) {
        return this.addCommentFiles(requestId, commentId, [file], options);
    },

    /**
     * Прикрепить несколько файлов к комментарию
     */
    async addCommentFiles(requestId, commentId, files, options = {}) {
        const results = [];
        for (const file of toFileArray(files)) {
            const formData = createSingleFileFormData('files', file, {
                suppressNotification: options.suppressNotification ? 'true' : null
            });
            const { data } = await api.post(`/api/requests/items/${requestId}/comments/${commentId}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            results.push(data);
        }
        return mergeUploadResults(results);
    },

    /**
     * Удалить файл из комментария
     */
    deleteCommentFile(requestId, commentId, fileId) {
        return api.delete(`/api/requests/items/${requestId}/comments/${commentId}/files/${fileId}`).then(({ data }) => data);
    },

    /**
     * Сохранить черновик
     */
    saveDraft(requestTypeId, draftData) {
        return api.post(`/api/requests/drafts`, {
            request_type_id: requestTypeId,
            draft_data: draftData
        }).then(({ data }) => data);
    },

    /**
     * Удалить черновик по типу заявки
     */
    deleteDraftByType(requestTypeId) {
        return api.delete(`/api/requests/drafts/type/${requestTypeId}`).then(({ data }) => data);
    }
};
